/**
 * OurStoryPage
 * /our-story
 *
 * Real origin timeline with specific, non-generic language.
 * Organization + WebPage schema.
 */

'use client';

import { useReducedMotion, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { organizationSchema, webPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import { AnimatedSection, StaggerContainer, StaggerItem } from '@/components/marketing';
import {
  ArrowRight,
  Megaphone,
  Target,
  Database,
  Stack,
  Lightning,
  Users,
  CheckCircle,
  TrendUp,
  MapPin,
} from '@phosphor-icons/react';

const timeline = [
  {
    year: '2022',
    icon: Megaphone,
    title: 'The marketing agency',
    description:
      'We started as Rogue Move Digital Agency, running Google Ads, Facebook campaigns, and SEO for roofing contractors across the US. Our job was to generate leads. Our frustration was watching them disappear into CRMs that could not connect a click to a closed job.',
  },
  {
    year: '2023',
    icon: Target,
    title: 'The discovery',
    description:
      'We saw the same problem everywhere: leads went into a CRM and stalled. Attribution stopped at the form submit. Follow-ups were manual. There was no way to tie ad spend to signed contracts. The CRMs were built by developers who had never run a roofing campaign.',
  },
  {
    year: '2024',
    icon: Database,
    title: 'The lead-tracking hub',
    description:
      'We built the first Rooflyzer tool: a lead attribution system that tracked every touchpoint from first click to signed contract. For the first time, our agency clients could see which marketing channels actually produced revenue, not just form fills.',
  },
  {
    year: '2025',
    icon: Stack,
    title: 'The CRM pivot',
    description:
      'Clients kept asking for more: invoicing, scheduling, estimates, proposals, and marketing automation. We added each module only after it shared the same data model as the lead tracker. The platform became a single operating system instead of a bundle of disconnected features.',
  },
  {
    year: '2026',
    icon: Lightning,
    title: 'Rooflyzer OS',
    description:
      'Today Rooflyzer OS is a complete platform for roofing businesses: lead management, satellite estimates, proposal presenter, marketing automation, storm intelligence, invoicing, and project management. All built by the agency that understands the entire customer journey.',
  },
];

const milestones = [
  {
    label: 'First contractor onboarded',
    value: '2024',
    desc: 'A storm restoration company in Texas became our first paying customer.',
  },
  {
    label: 'Modules live',
    value: '8',
    desc: 'Lead management, estimates, proposals, marketing, storm, project, invoicing, and documents.',
  },
  {
    label: 'Founding-member pricing',
    value: '$0-99',
    desc: 'Per user per month, no long-term contract required.',
  },
];

function Reveal({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export const OurStoryPage = () => {
  const orgSchema = {
    '@context': 'https://schema.org',
    ...organizationSchema,
  };

  const pageSchema = webPageSchema({
    name: 'Our Story — From Marketing Agency to Roofing Operating System',
    description:
      'How a roofing marketing agency evolved into Rooflyzer OS, an AI-ready operating system built for roofing contractors.',
    url: '/our-story',
    datePublished: '2026-01-01',
    dateModified: '2026-06-01',
  });

  return (
    <>
      <SEOHead
        title="Our Story — From Marketing Agency to Roofing Operating System"
        description="We didn't start as software developers. We started as an agency tired of watching our clients' leads die in clunky, overpriced systems. Here's how Rooflyzer was born."
        canonicalUrl="/our-story"
        keywords="rooflyzer story, roofing CRM origin, rogue move digital agency, marketing agency CRM"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20 relative">
        <div className="absolute inset-0 tech-grid-bg opacity-[0.06] pointer-events-none z-0" />
        <div className="max-w-5xl mx-auto relative z-10">
          <Breadcrumbs items={[{ name: 'Our Story', url: '/our-story' }]} className="mb-8" />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-24">
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-6">
                <Users className="size-3.5 text-emerald-400" weight="fill" />
                <span>Our Story</span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-8 tracking-tight">
                Built from <span className="text-white">necessity</span>
              </h1>
              <p className="text-lg sm:text-xl text-white/70 leading-relaxed max-w-3xl mx-auto">
                We didn't start as software developers. We started as performance marketers tired of
                watching our clients' leads die in clunky, overpriced systems. We saw the gaps in
                legacy CRM databases, so we evolved. Rooflyzer moved from a performance marketing
                agency to a lead-tracking hub, and finally into a full-scale operating system
                designed to eliminate fragmented data that slows roofers down.
              </p>
            </AnimatedSection>
          </section>

          {/* Chronological timeline */}
          <section className="mb-24 relative">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-slate-800 opacity-60 md:-translate-x-px z-0 hidden sm:block" />

            <div className="space-y-16">
              {timeline.map((item, i) => {
                const isRight = i % 2 === 1;
                return (
                  <Reveal key={i} delay={i * 0.1}>
                    <div
                      className={`relative flex flex-col md:flex-row items-start ${isRight ? 'md:flex-row-reverse' : ''} z-10`}
                    >
                      <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-slate-950 border-2 border-slate-400 z-20 hidden sm:block" />

                      <div className="ml-16 md:ml-0 md:w-[calc(50%_-_2.5rem)] bg-slate-900/60 rounded-2xl p-6 sm:p-8 border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:-translate-y-1">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center">
                            <item.icon className="size-6 text-emerald-400" weight="fill" />
                          </div>
                          <div>
                            <span className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-400">
                              {item.year}
                            </span>
                            <h3 className="text-xl font-bold text-white mt-0.5">{item.title}</h3>
                          </div>
                        </div>
                        <p className="text-white/70 text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </section>

          {/* Milestones */}
          <section className="mb-24">
            <AnimatedSection className="text-center mb-12">
              <h2 className="section-h2 font-bold text-white mb-3">Where we are today</h2>
              <p className="text-white/55 body-lg">Concrete milestones, not vanity metrics.</p>
            </AnimatedSection>

            <StaggerContainer className="grid sm:grid-cols-3 gap-6">
              {milestones.map((m, i) => (
                <StaggerItem key={i}>
                  <div className="bg-slate-900/60 rounded-2xl p-6 text-center border border-slate-800 hover:border-slate-700 transition-all duration-300 h-full">
                    <p className="text-3xl font-bold text-white mb-2">{m.value}</p>
                    <p className="text-white font-semibold text-sm mb-1">{m.label}</p>
                    <p className="text-white/50 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </section>

          {/* Marketing agency DNA */}
          <section className="mb-24 bg-slate-900/60 rounded-3xl p-8 sm:p-12 border border-slate-800 relative overflow-hidden">
            <AnimatedSection className="relative z-10">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700/80 flex items-center justify-center flex-shrink-0">
                  <TrendUp className="w-8 h-8 text-emerald-400" weight="fill" />
                </div>
                <div className="flex-1">
                  <h2 className="section-h2 font-bold text-white mb-4">
                    The agency DNA we never lost
                  </h2>
                  <p className="text-white/70 text-sm leading-relaxed mb-6">
                    Every feature in Rooflyzer is still judged by one question: does it help the
                    contractor close more jobs? We think in cost-per-acquisition, lead-to-close
                    rate, and return on ad spend. That is why our CRM captures marketing parameters
                    natively, why our pipeline tracks why leads stall, and why our invoicing
                    connects back to the campaign that produced the job.
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-3 text-white/80 text-sm">
                    {[
                      'UTM and source attribution built in',
                      'Re-nurture queue for stalled leads',
                      'Campaign ROI by channel and ad group',
                      'Automated review requests after job completion',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2.5">
                        <CheckCircle
                          className="size-5 text-emerald-400 flex-shrink-0 mt-0.5"
                          weight="fill"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </AnimatedSection>
          </section>

          {/* Location note */}
          <section className="mb-24">
            <Reveal>
              <div className="bg-slate-900/60 rounded-2xl p-8 border border-slate-800 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-slate-700/80 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-7 h-7 text-emerald-400" weight="fill" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">
                    Headquartered in Sheridan, Wyoming
                  </h3>
                  <p className="text-white/55 text-sm">
                    30 N Gould St #55327, Sheridan, WY 82801. Distributed team across the US.
                  </p>
                </div>
                <Link to="/contact">
                  <Button className="bg-white text-slate-950 hover:bg-slate-100 font-semibold rounded-lg px-6">
                    Contact us
                    <ArrowRight className="ml-2 size-4" />
                  </Button>
                </Link>
              </div>
            </Reveal>
          </section>

          {/* Final CTA */}
          <section className="text-center">
            <AnimatedSection>
              <h2 className="section-h2 font-bold text-white mb-4">
                See the platform that grew out of this story
              </h2>
              <p className="text-white/55 body-lg max-w-2xl mx-auto mb-8">
                We built Rooflyzer because no other tool could do the job. Book a demo and we'll
                show you exactly how.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/demo">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 rounded-lg shadow-sm"
                  >
                    Book a demo
                    <ArrowRight className="ml-2 size-5" />
                  </Button>
                </Link>
                <Link to="/features">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 rounded-lg px-8"
                  >
                    Explore features
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </section>
        </div>
      </div>
    </>
  );
};
